import MomentUtils from "@date-io/moment";
import {
    CssBaseline,
    MuiThemeProvider,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from "moment";
import { SnackbarProvider } from "notistack";
import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { Route, Switch, useLocation } from "react-router-dom";
import FullscreenLoader from "./components/layout/FullscreenLoader";
import Layout from "./components/layout/Layout";
import ManageAuctionDetails from "./components/pages/admin/ManageAuctionDetails/ManageAuctionDetails";
import ManageAuctionList from "./components/pages/admin/ManageAuctionList";
import ManageUserDetails from "./components/pages/admin/ManageUserDetails";
import ManageUserList from "./components/pages/admin/ManageUserList";
import ForgotPassword from "./components/pages/ForgotPassword";
import Login from "./components/pages/Login";
import Logout from "./components/pages/Logout";
import Parcels from "./components/pages/Parcels";
import PasswordReset from "./components/pages/PasswordReset";
import Profile from "./components/pages/Profile";
import Register from "./components/pages/Register";
import AuthRoute from "./components/shared/AuthRoute";
import { AppInsightsLogger } from "./components/shared/logging/AppInsightsLogger";
import Constants from "./models/utility/Constants";
import { LoadState } from "./models/utility/LoadState";
import SecurityProvider from "./providers/SecurityProvider";
import * as AppConfigStore from "./store/AppConfigStore";
import * as TimerStore from "./store/TimerStore";
import * as UserStore from "./store/UserStore";
import createGlobalTheme from "./styles";
import * as SnackbarCss from "./styles/SnackbarCss";
import NotFound from "./components/pages/NotFound";
import posthog from "posthog-js";
import { bindActionCreators, Dispatch } from "redux";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { useGetConfigQuery } from "./api/configApiSlice";
import { IAppState } from "./store";
import { useMetaPixel } from "./hooks/useMetaPixel";
import TermsAcceptanceDialog from "./components/dialogs/TermsAcceptanceDialog";
import { useStackAdaptPixel } from "./hooks/useStackAdaptPixel";

const globalTheme = createGlobalTheme();
const styles = () => SnackbarCss.styles(globalTheme);

type IAppProps = UserStore.IUserStoreState &
    typeof UserStore.actionCreators &
    WithStyles<typeof SnackbarCss.styles>;

const SNACKBAR_AUTO_HIDE_DURATION = 15000;
const SNACKBAR_MAX_STACK_DESKTOP = 3;
const SNACKBAR_MAX_STACK_MOBILE = 1;
const SNACKBAR_SCREEN_WIDTH_BREAKPOINT = 600;

function App({ classes }: IAppProps) {
    const dispatch = useAppDispatch();
    const userStore = useAppSelector((state) => state.userStoreState);
    const location = useLocation();

    const userEmail = userStore.userProfile?.email;

    const getSnackbarMaxStack = () =>
        window.innerWidth > SNACKBAR_SCREEN_WIDTH_BREAKPOINT
            ? SNACKBAR_MAX_STACK_DESKTOP
            : SNACKBAR_MAX_STACK_MOBILE;

    const [snackbarMaxStack, setSnackbarMaxStack] = useState(
        getSnackbarMaxStack()
    );

    window.addEventListener("resize", () => {
        setSnackbarMaxStack(getSnackbarMaxStack());
    });

    // 2024-07-02 Jerremy Lewis: Load app config in two ways to support new
    //   and old store. Should remove after all uses of old store are removed.
    const getConfigQuery = useGetConfigQuery(); // New
    useEffect(() => {
        dispatch(UserStore.actionCreators.loadUserFromStoredToken());
        dispatch(AppConfigStore.actionCreators.loadAppConfig()); // Old
    }, [dispatch]);

    useEffect(() => {
        if (getConfigQuery.data) {
            ReactGA.initialize(getConfigQuery.data.analyticsKey);
            ReactGA.set({ userId: userEmail });
            ReactGA.set({
                dimension1: userEmail ? "registered" : "anonymous"
            });

            AppInsightsLogger.init({
                authenticatedUserId: userEmail ?? null,
                instrumentationKey: getConfigQuery.data.instrumentationKey
            });
        }
    }, [getConfigQuery.data, userEmail]);

    useMetaPixel({ metaPixelId: getConfigQuery.data?.metaPixelId });
    useStackAdaptPixel({
        stackAdaptPixelId: getConfigQuery.data?.stackAdaptPixelId
    });

    useEffect(() => {
        dispatch(UserStore.actionCreators.loadUserFromStoredToken());
        dispatch(TimerStore.actionCreators.getServerTime());
        dispatch(
            TimerStore.actionCreators.startClientInterval(() => {
                dispatch(TimerStore.actionCreators.tickClientClocks());
            })
        );

        return () => {
            dispatch(TimerStore.actionCreators.stopClientInterval());
        };
    }, [dispatch]);

    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: location.pathname + location.search
        });
        AppInsightsLogger.trackPageView();
        posthog.capture("$pageview");
    }, [location.pathname, location.search]);

    return (
        <MuiThemeProvider theme={globalTheme}>
            <SnackbarProvider
                maxSnack={snackbarMaxStack}
                preventDuplicate={true}
                classes={{
                    anchorOriginTopLeft: classes.anchorTopLeft
                }}
                autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
            >
                <MuiPickersUtilsProvider
                    utils={MomentUtils}
                    libInstance={moment}
                >
                    {userStore.authLoadState === LoadState.NotLoaded ? (
                        <FullscreenLoader hideTempBody={true} />
                    ) : (
                        <>
                            <Layout>
                                <Switch>
                                    {/* Publically Accessible */}
                                    <Route
                                        exact={true}
                                        path={Constants.Routes.ForgotPassword()}
                                        component={ForgotPassword}
                                    />
                                    <Route
                                        exact={true}
                                        path={Constants.Routes.Login()}
                                        component={Login}
                                    />
                                    <Route
                                        exact={true}
                                        path={Constants.Routes.Logout()}
                                        component={Logout}
                                    />
                                    <Route
                                        exact={true}
                                        path={Constants.Routes.Parcels()}
                                        component={Parcels}
                                    />
                                    <Route
                                        exact={true}
                                        path={Constants.Routes.PasswordReset()}
                                        component={PasswordReset}
                                    />
                                    <AuthRoute
                                        exact={true}
                                        path={Constants.Routes.Profile()}
                                        component={Profile}
                                    />
                                    <Route
                                        exact={true}
                                        path={Constants.Routes.Register()}
                                        component={Register}
                                    />

                                    {/* Admins Only */}
                                    <AuthRoute
                                        exact={true}
                                        path={Constants.Routes.AdminAuctionList()}
                                        permission={
                                            SecurityProvider.CanManageAuctionsAndParcels
                                        }
                                        component={ManageAuctionList}
                                    />
                                    <AuthRoute
                                        exact={true}
                                        path={Constants.Routes.AdminAuctionDetail()}
                                        permission={
                                            SecurityProvider.CanManageAuctionsAndParcels
                                        }
                                        component={ManageAuctionDetails}
                                    />
                                    <AuthRoute
                                        exact={true}
                                        path={Constants.Routes.AdminUserList()}
                                        permission={
                                            SecurityProvider.CanManageUsers
                                        }
                                        component={ManageUserList}
                                    />
                                    <AuthRoute
                                        exact={true}
                                        path={Constants.Routes.AdminUserDetail()}
                                        permission={
                                            SecurityProvider.CanManageUsers
                                        }
                                        component={ManageUserDetails}
                                    />

                                    {/* When no routes match show a 404 page */}
                                    <Route component={NotFound} />
                                </Switch>
                            </Layout>
                            <TermsAcceptanceDialog />
                        </>
                    )}
                    <CssBaseline />
                </MuiPickersUtilsProvider>
            </SnackbarProvider>
        </MuiThemeProvider>
    );
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.userStoreState, state.appConfigStoreState);
const reduxDispatch = (dispatch: Dispatch) =>
    bindActionCreators(
        Object.assign(
            {},
            UserStore.actionCreators,
            AppConfigStore.actionCreators
        ),
        dispatch
    );

export default withStyles(styles)(connect(reduxState, reduxDispatch)(App));
